import React from 'react'
import { Truck } from 'lucide-react'
import ImageSlideshow from './ImageSlideshow'
import LoadingRing from './LoadingRing'
import { IProductImage } from '../types'
import { ICustomComponent, IEstimatedDispatchDays, IProduct } from '@/context/ProductDataStore'

interface ProductImageSectionProps {
    isLoading: boolean
    images: any[]
    activeProductImage: IProductImage
    showEditColors: boolean
    onEditColors: () => void
    onAllImagesLoaded: () => void
    estimatedDispatchDays?: IEstimatedDispatchDays | null
    isPreorder?: boolean
    specialLogo?: string
    specialLogoSize?: string
    customHero?: {
        backgroundUrl?: string
        customColourProduct: IProduct
        customComponents: ICustomComponent[]
    }
}

const ProductImageSection: React.FC<ProductImageSectionProps> = ({
    isLoading,
    images,
    activeProductImage,
    showEditColors,
    onEditColors,
    onAllImagesLoaded,
    estimatedDispatchDays,
    isPreorder = false,
    specialLogo,
    specialLogoSize,
    customHero
}) => {
    const formatDispatchEstimate = (estimate?: IEstimatedDispatchDays | null): string | null => {
        if (!estimate) {
            return null
        }

        const { min, max, unit } = estimate
        const hasMin = typeof min === 'number'
        const hasMax = typeof max === 'number'
        if (!hasMin && !hasMax) {
            return null
        }

        const unitLabel = unit === 'business_days'
            ? 'workdays'
            : unit
                ? unit.replace(/_/g, ' ')
                : 'days'
        if (hasMin && hasMax && min !== max) {
            return `${min}-${max} ${unitLabel}`
        }
        const value = hasMin ? min : max
        return `${value} ${unitLabel}`
    }

    const dispatchEstimateLabel = formatDispatchEstimate(estimatedDispatchDays)

    // console.log('images', images)
    // console.log('activeProductImage', activeProductImage)
    return (
        <div className="w-full md:flex-grow md:sticky md:top-[90px] self-start">
            {isLoading ? (
                <div className="flex items-center justify-center aspect-[4/4.25]">
                    <LoadingRing size="lg" />
                </div>
            ) : (
                <div className='relative'>
                    <ImageSlideshow
                        images={images}
                        dynamicImage={activeProductImage}
                        className="flex flex-col w-full"
                        showEditColors={showEditColors}
                        onEditColors={onEditColors}
                        onAllImagesLoaded={onAllImagesLoaded}
                        customHero={customHero}
                    />
                    {specialLogo && (
                        <div className="absolute top-3 right-3 z-10 md:top-4 md:right-4 pointer-events-none">
                            <img
                                src={specialLogo}
                                alt=""
                                aria-hidden="true"
                                className={`${specialLogoSize || 'size-24'} object-contain`}
                            />
                        </div>
                    )}
                    <div className="slideshow-shipping-info absolute w-min text-nowrap font-headline flex items-center gap-1.5 bg-white rounded-lg md:py-2 md:px-3 md:top-3 md:left-3 py-1 px-2 top-2 left-2 text-sm">
                        <div className="overflow-hidden">
                            <Truck className="size-3.5 animate-truck-animation" />
                        </div>{' '}
                        <div className='mb-0.5'>
                            {isPreorder
                                ? "Sold Out - Available for Preorder"
                                : `Ships in ${dispatchEstimateLabel || '5 days'}`}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProductImageSection
